import { Controller, Path, useFormContext } from 'react-hook-form'
import { FormControl } from '@/components/form-control'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { professionOptions } from '@/modules/surveys/configs/onboarding/personal-info-config'
import { cn } from '@/lib/css-utils'

interface ProfessionSelectProps<T extends Record<string, unknown>> {
  name: Path<T>
  selectClassName?: string
  variant?: 'filter' | 'select'
  onSelectProfession?: (profession: string | null) => void
  label?: string
}

export function ProfessionSelect<T extends Record<string, unknown>>({
  name,
  selectClassName,
  variant = 'filter',
  onSelectProfession,
  label,
}: ProfessionSelectProps<T>) {
  const { control } = useFormContext<T>()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => {
        return (
          <FormControl
            label={label}
            className="min-w-52"
            errorText={fieldState.error?.message}
          >
            <Select
              value={value as string}
              onValueChange={(profession: string | null) => {
                onChange(profession)
                onSelectProfession?.(profession)
              }}
            >
              <SelectTrigger
                id="profession-select"
                aria-label="Select Profession"
                className={cn(
                  {
                    'border-t-0 border-x-0 border-b border-solid \
                  border-background-primary px-3 py-2 shadow-none rounded-none \
                  focus:outline-0 focus:shadow-none focus:border-b focus:border-b-dropdown-border-secondary \
                  overflow-hidden hover:border-b hover:border-b-dropdown-border-secondary \
                  dark:hover:border-b-dropdown-border-secondary \
                  text-md h-fit \
                  pl-7': variant === 'filter',
                  },
                  selectClassName,
                )}
                hasError={!!fieldState.error?.message}
              >
                <SelectValue placeholder="Select profession" />
              </SelectTrigger>
              <SelectContent>
                {professionOptions.map(({ slug, label }) => (
                  <SelectItem
                    value={slug}
                    key={slug}
                    className="text-md md:text-sm"
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        )
      }}
    />
  )
}
